import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import Swal from 'sweetalert2';
import { GET_ACTIVE_SENT_REFERRALS } from '../gql';
import ActiveSentReferralsTable from '../tables/ActiveSentReferralsTable';
import { usePromoteReferral } from '../../referral/redux/promoteReferral';
import clientGraphql from '../../../common/apollo-graphql';
import { DELETE_REFERRAL_MUTATION } from '../../referral/gql/Mutation';
import { REFERRAL_REFRESH_SUBSCRIPTION } from '../../referral/gql/Subscription';
import ActiveReferrals from './common/ActiveReferrals';
import ActiveReferralBaseDialogs from './common/ActiveReferralBaseDialogs';
import useActiveReferralDialogState from './common/useActiveReferralDialogState';

export default function ActiveSentReferrals({ history, uuid }) {
  const { promoteReferral } = usePromoteReferral();
  const [deleteReferral] = useMutation(DELETE_REFERRAL_MUTATION, { client: clientGraphql });

  const dialogs = useActiveReferralDialogState({
    promoteReferral,
    promoteStatus: 'ref_completed',
    successText: (data) => {
      const tab = data === 'Referral Completed' ? 'Declined' : 'Referral History';
      return `This referral has been moved to your ${tab} tab`;
    },
  });
  const {
    actionLoading,
    setActionLoading,
    refresh,
    openNotesFor,
    openReferralEditor,
    openSentDateEditor,
    openAppointmentFor,
    handleOnComplete,
    setRefresh,
  } = dialogs;

  const handleOnDelete = useCallback(
    (rowId) => {
      Swal.fire({
        title: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.isConfirmed) {
          setActionLoading(true);
          deleteReferral({ variables: { referralUuid: rowId } })
            .then(() => refresh())
            .catch(() => Swal.fire({ icon: 'error', text: 'Failed to delete referral!', showConfirmButton: true }))
            .finally(() => setActionLoading(false));
        }
      });
    },
    [deleteReferral, refresh, setActionLoading]
  );

  const buildActions = useCallback(
    (item) => ({
      onViewNotes: () => openNotesFor(item.id),
      onUpdate: () => history.push(`/referrals/${item.id}?edit=1`),
      onViewReferral: () => history.push(`/referrals/${item.id}/review`),
      onUpdateReferral: () => openReferralEditor(item),
      onUpdateSentDate: () => openSentDateEditor(item),
      onComplete: () => handleOnComplete(item),
      onSetAppointment: () =>
        openAppointmentFor({
          id: item.id,
          appointment: item?.appointment,
          nextStatus: item.status,
        }),
      onDelete: () => handleOnDelete(item.id),
    }),
    [handleOnComplete, handleOnDelete, history, openAppointmentFor, openNotesFor, openReferralEditor, openSentDateEditor]
  );

  return (
    <ActiveReferrals
      query={GET_ACTIVE_SENT_REFERRALS}
      uuid={uuid}
      subscription={REFERRAL_REFRESH_SUBSCRIPTION}
      tableComponent={ActiveSentReferralsTable}
      buildActions={buildActions}
      withFaxFilter={true}
      initialSearchFilters={{
        type: 'Name',
        patientName: '',
        birthdate: '',
        filterFax: 'all',
      }}
      isBusy={actionLoading}
      notifyOnNetworkStatusChange={true}
      shouldShowGlobalLoader={({ networkStatus, isBusy: busy }) => networkStatus === 1 || busy}
      renderDialogs={({ refresh }) => {
        setRefresh(refresh);
        return <ActiveReferralBaseDialogs history={history} dialogs={dialogs} isReceived={false} />;
      }}
    />
  );
}
